import { apiUrl } from "../api/endpoints";
import React, { useState, useEffect, useContext } from 'react';
import '../Components/modal.css';
import axios from 'axios';

import { useAuth } from '../hooks/AuthProvider';
import Alert from '../Components/Alert';

import { LanguageContext } from '../context/LanguageContext';
import roleTranslations from '../languages/role.json';
import { getErrorMessage } from '../utils/errorUtils';
import { AddNewIcon, NameIcon } from '../svg/UtilsIcons';
import Spinner from '../Components/Spinner';

export const EditRole = ({ roleId, refresh, setState }) => {
  const { permissions } = useAuth();
  const { lang } = useContext(LanguageContext);

  const canEditRoles = permissions['edit_roles'];

  const t = roleTranslations[lang]?.editRole || {};
  const isRtl = ['ar', 'ur'].includes(lang);

  const [formData, setFormData] = useState({
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialName, setInitialName] = useState('');

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right'
  });

  const showAlert = (type, message) => {
    setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
  };

  useEffect(() => {
    if (!canEditRoles || !roleId) {
      return;
    }

    const fetchRole = async () => {
      setLoading(true);
      try {
        const res = await axios.get(apiUrl(`roles/${roleId}`));
        if (res.data.status === 'OK' && res.data.data) {
          setFormData({ name: res.data.data.name });
          setInitialName(res.data.data.name);
        } else {
          throw new Error('Role not found or invalid response.');
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        showAlert('error', `Failed to load role details: ${errorMessage}`);
        setTimeout(() => setState(false), 1500);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [canEditRoles, roleId, setState]);

  const updateRole = async () => {
    if (loading) return;

    const newName = formData.name.trim();

    if (initialName === 'Owner') {
      showAlert(
        'error',
        t.validation?.ownerImmutable || 'The "Owner" role cannot be modified.'
      );
      return;
    }

    if (newName === initialName) {
      showAlert('info', t.validation?.noChanges || 'No changes made.');
      setTimeout(() => setState(false), 1500);
      return;
    }

    if (!newName) {
      showAlert('error', t.validation?.nameRequired || 'Role name is required.');
      return;
    }

    const body = { name: newName };

    setLoading(true);
    try {
      const res = await axios.put(
        apiUrl(`roles/${roleId}`),
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );

      showAlert(
        'success',
        res.data.message || t.successMessage || 'Role updated successfully.'
      );

      refresh();

      setTimeout(() => {
        setState(false);
      }, 1500);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      showAlert('error', errorMessage);
      setLoading(false);
    }
  };


  if (!canEditRoles) {
    return (
      <div
        id="modal"
        style={{ direction: isRtl ? 'rtl' : 'ltr' }}
      >
        <div
          id="popup"
          className="dashboard-modal"
          style={{
            animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
            fontSize:
              'calc(var(--body-font-size) * var(--font-size-multiplier))'
          }}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <AddNewIcon />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.permissionDenied?.title || 'Permission Denied'}</h2>
              <p>
                {t.permissionDenied?.message ||
                  'You do not have permission to edit roles.'}
              </p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content" />

          <div className="modal-footer dashboard-footer">
            <button
              className="btn btn-cancel"
              onClick={() => setState(false)}
            >
              {t.buttonClose || 'Close'}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }


  return (
    <div
      id="modal"
      style={{ direction: isRtl ? 'rtl' : 'ltr' }}
    >
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div
        id="popup"
        className="dashboard-modal"
        style={{
          animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
          fontSize:
            'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="dashboard-header">
          <div className="dashboard-header-icon">
            <AddNewIcon />
          </div>
          <div className="dashboard-header-text">
            <h2>{t.title || 'Edit Role'}</h2>
            <p>
              {t.subtitle ||
                t.description ||
                (initialName
                  ? `Update the access role "${initialName}".`
                  : 'Update an existing access role.')}
            </p>
          </div>
        </div>

        <div className="modal-divider" />

        {loading ? (
          <div
            className="modal-content dashboard-content"
            style={{ minHeight: 200, display: 'flex', alignItems: 'center' }}
          >
            <Spinner />
          </div>
        ) : (
          <div className="modal-content dashboard-content">
            {initialName === 'Owner' && (
              <div
                style={{
                  color: 'red',
                  fontWeight: 'bold',
                  marginBottom: '16px'
                }}
              >
                {t.ownerWarning ||
                  'NOTE: The "Owner" role cannot be modified or deleted.'}
              </div>
            )}

            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-icon">
                  <NameIcon />
                </span>
                <span className="form-section-title-text">
                  {t.labelRoleName || 'Role Name'}
                </span>
              </div>

              <div className="form-item">
                <div className="field-with-counter">
                  <input
                    type="text"
                    className="add-column"
                    name="name"
                    placeholder={t.placeholderName || 'Enter Role name'}
                    maxLength={50}
                    value={formData.name}
                    onChange={e =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    disabled={loading || initialName === 'Owner'}
                  />
                  <span className="field-counter">
                    {(formData.name?.length || 0)} / 50
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer dashboard-footer">
          <button
            className="btn btn-cancel"
            onClick={() => setState(false)}
            disabled={loading}
          >
            {t.buttonCancel || 'Cancel'}
          </button>
          <button
            className="btn btn-apply dashboard-primary-btn"
            onClick={updateRole}
            disabled={loading || initialName === 'Owner'}
          >
            {loading ? '...' : t.buttonSave || 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
